<script setup>
import { ref, onMounted, computed } from 'vue'

// 太阳活动和地磁数据
const solarFluxIndex = ref(120)
const kIndex = ref(2)
const timeOfDay = ref('day')

// 波段数据
const bands = ref([
  { id: '160m', name: '160米', freq: '1.8-2.0 MHz', condition: '', distance: '', reliability: 0 },
  { id: '80m', name: '80米', freq: '3.5-4.0 MHz', condition: '', distance: '', reliability: 0 },
  { id: '60m', name: '60米', freq: '5.3-5.4 MHz', condition: '', distance: '', reliability: 0 },
  { id: '40m', name: '40米', freq: '7.0-7.3 MHz', condition: '', distance: '', reliability: 0 },
  { id: '30m', name: '30米', freq: '10.1-10.15 MHz', condition: '', distance: '', reliability: 0 },
  { id: '20m', name: '20米', freq: '14.0-14.35 MHz', condition: '', distance: '', reliability: 0 },
  { id: '17m', name: '17米', freq: '18.068-18.168 MHz', condition: '', distance: '', reliability: 0 },
  { id: '15m', name: '15米', freq: '21.0-21.45 MHz', condition: '', distance: '', reliability: 0 },
  { id: '12m', name: '12米', freq: '24.89-24.99 MHz', condition: '', distance: '', reliability: 0 },
  { id: '10m', name: '10米', freq: '28.0-29.7 MHz', condition: '', distance: '', reliability: 0 },
  { id: '6m', name: '6米', freq: '50-54 MHz', condition: '', distance: '', reliability: 0 },
  { id: '2m', name: '2米', freq: '144-148 MHz', condition: '', distance: '', reliability: 0 },
  { id: '70cm', name: '70厘米', freq: '430-440 MHz', condition: '', distance: '', reliability: 0 }
])

// 实时太阳数据
const solarData = ref({
  sfi: null,
  kIndex: null,
  sunspots: null,
  aIndex: null,
  xray: null,
  geomagField: null,
  updated: null,
  source: null,
  loading: true,
  error: null
})

// 添加一个新的状态来控制刷新按钮的加载状态
const refreshing = ref(false)

// 计算当前时间是白天还是夜晚
const currentDayNight = computed(() => {
  const now = new Date()
  const hours = now.getHours()
  // 简单判断：6点到18点为白天
  return hours >= 6 && hours < 18 ? 'day' : 'night'
})

// 获取实时太阳数据
async function fetchSolarData() {
  // 设置刷新状态，但不立即显示加载指示器
  refreshing.value = true
  
  // 如果是首次加载，才显示完整的加载状态
  if (!solarData.value.sfi) {
    solarData.value.loading = true
  }
  
  solarData.value.error = null
  
  try {
    // 首先尝试从我们的 API 获取数据
    const response = await fetch('http://114.132.46.152:8080/api/solar-data')
    
    if (!response.ok) {
      throw new Error('API请求失败')
    }
    
    const data = await response.json()
    
    // 更新数据
    solarData.value.sfi = data.sfi || 120
    solarData.value.kIndex = data.kIndex || 2
    solarData.value.sunspots = data.sunspots || 50
    solarData.value.aIndex = data.aIndex || 10
    solarData.value.xray = data.xray || 'A0.0'
    solarData.value.geomagField = data.geomagField || 'quiet'
    solarData.value.updated = data.updated || new Date().toISOString()
    solarData.value.source = data.source || '本地服务'
    
    // 更新表单值
    solarFluxIndex.value = solarData.value.sfi
    kIndex.value = solarData.value.kIndex
    
    // 更新传播预测
    updatePropagation()
    
    // 清除错误
    solarData.value.error = null
    
    console.log('从本地API获取的数据:', {
      sfi: solarData.value.sfi,
      kIndex: solarData.value.kIndex,
      sunspots: solarData.value.sunspots,
      aIndex: solarData.value.aIndex,
      xray: solarData.value.xray,
      geomagField: solarData.value.geomagField,
      updated: solarData.value.updated
    })
    
  } catch (error) {
    console.error('本地API获取失败，尝试备用方法:', error)
    
    // 如果本地API失败，尝试使用原来的方法
    try {
      // 使用代理服务解决CORS问题
      const proxyUrl = 'https://api.allorigins.win/get?url='
      const targetUrl = encodeURIComponent('https://www.hamqsl.com/solarxml.php')
      
      const response = await fetch(proxyUrl + targetUrl)
      
      if (!response.ok) {
        throw new Error('备用API请求失败')
      }
      
      const data = await response.json()
      
      if (!data.contents) {
        throw new Error('代理返回的数据无效')
      }
      
      const xmlText = data.contents
      
      // 解析XML数据
      const parser = new DOMParser()
      const xmlDoc = parser.parseFromString(xmlText, 'text/xml')
      
      // 提取SFI数据
      const sfiElement = xmlDoc.querySelector('solarflux')
      if (sfiElement) {
        solarData.value.sfi = parseInt(sfiElement.textContent) || 120
      } else {
        throw new Error('无法获取SFI数据')
      }
      
      // 提取K指数
      const kIndexElement = xmlDoc.querySelector('kindex')
      if (kIndexElement) {
        solarData.value.kIndex = parseInt(kIndexElement.textContent) || 2
      } else {
        console.warn('无法获取K指数，使用默认值')
        solarData.value.kIndex = 2
      }
      
      // 提取太阳黑子数
      const sunspotElement = xmlDoc.querySelector('sunspots')
      if (sunspotElement) {
        solarData.value.sunspots = parseInt(sunspotElement.textContent) || 50
      }
      
      // 提取A指数
      const aIndexElement = xmlDoc.querySelector('aindex')
      if (aIndexElement) {
        solarData.value.aIndex = parseInt(aIndexElement.textContent) || 10
      }
      
      // 提取X射线强度
      const xrayElement = xmlDoc.querySelector('xray')
      if (xrayElement) {
        solarData.value.xray = xrayElement.textContent || 'A0.0'
      }
      
      // 提取地磁场状态
      const geomagFieldElement = xmlDoc.querySelector('geomagfield')
      if (geomagFieldElement) {
        solarData.value.geomagField = geomagFieldElement.textContent || 'quiet'
      }
      
      // 提取更新时间
      const updatedElement = xmlDoc.querySelector('updated')
      if (updatedElement) {
        solarData.value.updated = updatedElement.textContent || new Date().toISOString()
      } else {
        solarData.value.updated = new Date().toISOString()
      }
      
      // 提取数据来源
      const sourceElement = xmlDoc.querySelector('source')
      if (sourceElement) {
        solarData.value.source = sourceElement.textContent || 'HamQSL'
      } else {
        solarData.value.source = 'HamQSL'
      }
      
      // 更新表单值
      solarFluxIndex.value = solarData.value.sfi
      kIndex.value = solarData.value.kIndex
      
      // 更新传播预测
      updatePropagation()
      
      // 清除错误
      solarData.value.error = null
      
      console.log('从HamQSL获取的数据:', {
        sfi: solarData.value.sfi,
        kIndex: solarData.value.kIndex,
        sunspots: solarData.value.sunspots,
        aIndex: solarData.value.aIndex,
        xray: solarData.value.xray,
        geomagField: solarData.value.geomagField,
        updated: solarData.value.updated
      })
      
    } catch (backupError) {
      console.error('备用方法也失败:', backupError)
      solarData.value.error = '无法获取实时太阳数据，请手动输入值'
      
      // 如果API失败，使用默认值
      updatePropagation()
    }
  } finally {
    // 延迟重置加载状态，避免闪烁
    setTimeout(() => {
      solarData.value.loading = false
      refreshing.value = false
    }, 300)
  }
}

// 移除不再使用的scrapeSolarDataFromSEPC函数

// 更新传播预测
function updatePropagation() {
  const sfi = parseInt(solarFluxIndex.value)
  const kIdx = parseInt(kIndex.value)
  const time = timeOfDay.value
  
  // 验证输入
  if (isNaN(sfi) || sfi < 60) solarFluxIndex.value = 60
  if (isNaN(kIdx) || kIdx < 0) kIndex.value = 0
  
  // 预测各波段传播状况
  bands.value = bands.value.map(band => {
    const prediction = predictBand(band.id, sfi, kIdx, time)
    return { ...band, ...prediction }
  })
}

// 预测单个波段
function predictBand(bandId, sfi, kIndex, timeOfDay) {
  let condition = ''
  let distance = ''
  let reliability = 0
  
  // 根据波段、太阳活动、地磁活动和时间预测传播状况
  switch (bandId) {
    case '160m':
      // 160米波段在白天吸收严重，夜晚较好
      if (timeOfDay === 'day') {
        condition = '差'
        distance = '近距离'
        reliability = 20
      } else {
        condition = kIndex <= 3 ? '良好' : '一般'
        distance = '中远距离'
        reliability = kIndex <= 2 ? 70 : 50
      }
      break
      
    case '80m':
      // 80米波段夜晚较好
      if (timeOfDay === 'day') {
        condition = '一般'
        distance = '近距离'
        reliability = 40
      } else {
        condition = kIndex <= 3 ? '良好' : '一般'
        distance = '中远距离'
        reliability = kIndex <= 2 ? 80 : 60
      }
      break
      
    case '60m':
      // 60米波段特性
      if (timeOfDay === 'day') {
        condition = '一般'
        distance = '中距离'
        reliability = 50
      } else {
        condition = kIndex <= 3 ? '良好' : '一般'
        distance = '中距离'
        reliability = kIndex <= 2 ? 75 : 60
      }
      break
      
    case '40m':
      // 40米波段全天可用，夜晚更好
      if (timeOfDay === 'day') {
        condition = '良好'
        distance = '中距离'
        reliability = 70
      } else {
        condition = kIndex <= 4 ? '优秀' : '良好'
        distance = '远距离'
        reliability = kIndex <= 3 ? 85 : 70
      }
      break
      
    case '30m':
      // 30米波段全天可用
      condition = kIndex <= 4 ? '良好' : '一般'
      distance = timeOfDay === 'day' ? '中距离' : '远距离'
      reliability = kIndex <= 3 ? 80 : 65
      break
      
    case '20m':
      // 20米波段白天最佳
      if (timeOfDay === 'day') {
        condition = sfi >= 100 && kIndex <= 4 ? '优秀' : '良好'
        distance = '远距离'
        reliability = sfi >= 120 && kIndex <= 3 ? 90 : 75
      } else {
        condition = sfi >= 120 ? '良好' : '一般'
        distance = '中远距离'
        reliability = sfi >= 130 ? 70 : 50
      }
      break
      
    case '17m':
    case '15m':
      // 17米和15米波段需要较高太阳活动
      if (sfi < 100) {
        condition = '差'
        distance = '近距离'
        reliability = 30
      } else if (timeOfDay === 'day') {
        condition = kIndex <= 4 ? '良好' : '一般'
        distance = '远距离'
        reliability = sfi >= 130 && kIndex <= 3 ? 85 : 65
      } else {
        condition = '差'
        distance = '近中距离'
        reliability = sfi >= 150 ? 40 : 20
      }
      break
      
    case '12m':
    case '10m':
      // 12米和10米波段需要高太阳活动
      if (sfi < 120) {
        condition = '差'
        distance = '近距离'
        reliability = 20
      } else if (timeOfDay === 'day') {
        condition = kIndex <= 3 ? '良好' : '一般'
        distance = '远距离'
        reliability = sfi >= 150 && kIndex <= 2 ? 80 : 60
      } else {
        condition = '差'
        distance = '近距离'
        reliability = 10
      }
      break
      
    case '6m':
      // 6米波段，受F2层和散射影响
      if (sfi >= 150 && timeOfDay === 'day') {
        condition = kIndex <= 3 ? '可能开放' : '不确定'
        distance = '远距离'
        reliability = 50
      } else {
        condition = '差'
        distance = '近距离'
        reliability = 20
      }
      break
      
    case '2m':
      // 2米波段主要是视距传播
      condition = '本地'
      distance = '视距'
      reliability = 90
      break
      
    case '70cm':
      // 70厘米波段主要是视距传播，可靠性更高但距离更短
      condition = '本地'
      distance = '短视距'
      reliability = 95
      break
      
    default:
      condition = '未知'
      distance = '未知'
      reliability = 0
  }
  
  return { condition, distance, reliability }
}

// 获取可靠性颜色
function getReliabilityColor(reliability) {
  if (reliability >= 80) {
    return '#4CAF50' // 绿色 - 非常好
  } else if (reliability >= 60) {
    return '#8BC34A' // 浅绿色 - 好
  } else if (reliability >= 40) {
    return '#FFC107' // 黄色 - 一般
  } else if (reliability >= 20) {
    return '#FF9800' // 橙色 - 较差
  } else {
    return '#F44336' // 红色 - 很差
  }
}

// 翻译地磁场状态
function translateGeomagField(status) {
  const translations = {
    'quiet': '平静',
    'unsettled': '不稳定',
    'active': '活跃',
    'minor storm': '小型风暴',
    'major storm': '大型风暴',
    'severe storm': '严重风暴'
  }
  
  return translations[status.toLowerCase()] || status
}

// 格式化为北京时间
function formatToBeijingTime(timeString) {
  if (!timeString) return '未知';
  
  try {
    // 解析原始时间字符串
    const date = new Date(timeString);
    
    // 检查日期是否有效
    if (isNaN(date.getTime())) {
      return timeString; // 如果解析失败，返回原始字符串
    }
    
    // 转换为北京时间 (UTC+8)
    const options = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
      timeZone: 'Asia/Shanghai'
    };
    
    return new Intl.DateTimeFormat('zh-CN', options).format(date);
  } catch (error) {
    console.error('时间格式化错误:', error);
    return timeString; // 出错时返回原始字符串
  }
}

// 组件挂载时获取数据并更新预测
onMounted(() => {
  // 设置默认时间（白天/夜晚）
  timeOfDay.value = currentDayNight.value
  
  // 尝试获取实时数据
  fetchSolarData().catch(() => {
    // 如果获取失败，使用默认值更新预测
    updatePropagation()
  })
})
</script>

<template>
  <div class="propagation-predictor">
    <h2>HF传播预测</h2>
    <p class="subtitle">根据太阳活动和地磁条件预测短波频段传播状况</p>
    
    <div class="input-section">
      <div class="card solar-data" v-if="solarData.loading">
        <div class="loading-indicator">
          <p>正在获取实时太阳数据...</p>
        </div>
      </div>
      
      <div class="card solar-data" v-else-if="solarData.error">
        <p class="error">{{ solarData.error }}</p>
      </div>
      
      <div class="card solar-data" v-else-if="solarData.sfi">
        <h4>实时太阳数据</h4>
        <div class="solar-info">
          <div class="solar-item">
            <span class="solar-value">SFI指数:{{ solarData.sfi }}</span>
          </div>
          <div class="solar-item">
            <span class="solar-value">K指数:{{ solarData.kIndex }}</span>
          </div>
          <div class="solar-item" v-if="solarData.sunspots">
            <span class="solar-value">太阳黑子数:{{ solarData.sunspots }}</span>
          </div>
          <div class="solar-item" v-if="solarData.geomagField">
            <span class="solar-value">地磁场状态:{{ translateGeomagField(solarData.geomagField) }}</span>
          </div>
          <div class="solar-item">
            <span class="solar-value">更新时间:{{ formatToBeijingTime(solarData.updated) }}</span>
          </div>
        </div>
        
        <button @click="fetchSolarData" class="refresh-btn" :disabled="refreshing">
          {{ refreshing ? '正在刷新...' : '刷新数据' }}
        </button>
      </div>
      
      <div class="card manual-inputs">
        <h4>传播参数</h4>
        <div class="input-group">
          <label for="sfi-input">SFI指数:</label>
          <div class="input-container">
            <input 
              type="number" 
              id="sfi-input" 
              v-model.number="solarFluxIndex" 
              min="60" 
              max="300"
              @change="updatePropagation"
            >
            <span class="input-hint">60-300</span>
          </div>
        </div>
        
        <div class="input-group">
          <label for="k-index-input">K指数:</label>
          <div class="input-container">
            <input 
              type="number" 
              id="k-index-input" 
              v-model.number="kIndex" 
              min="0" 
              max="9"
              @change="updatePropagation"
            >
            <span class="input-hint">0-9 (越低越好)</span>
          </div>
        </div>
        
        <div class="input-group">
          <label for="time-select">时间:</label>
          <div class="input-container">
            <select 
              id="time-select" 
              v-model="timeOfDay"
              @change="updatePropagation"
            >
              <option value="day">白天</option>
              <option value="night">夜晚</option>
            </select>
          </div>
        </div>
        
        <button @click="updatePropagation" class="update-btn">
          更新预测
        </button>
      </div>
    </div>
    
    <div class="card results-section">
      <h4>波段传播预测</h4>
      
      <!-- 桌面版表格 -->
      <div class="band-table desktop-only">
        <div class="band-header">
          <div class="band-cell">波段</div>
          <div class="band-cell">频率</div>
          <div class="band-cell">状况</div>
          <div class="band-cell">距离</div>
          <div class="band-cell">可靠性</div>
        </div>
        
        <div v-for="band in bands" :key="band.id" class="band-row">
          <div class="band-cell band-name">{{ band.name }}</div>
          <div class="band-cell band-freq">{{ band.freq }}</div>
          <div class="band-cell band-condition">{{ band.condition }}</div>
          <div class="band-cell band-distance">{{ band.distance }}</div>
          <div class="band-cell band-reliability">
            <div class="reliability-bar-container">
              <div 
                class="reliability-bar" 
                :style="{ width: band.reliability + '%', backgroundColor: getReliabilityColor(band.reliability) }"
              ></div>
              <div class="reliability-text">{{ band.reliability }}%</div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- 移动端卡片式布局 -->
      <div class="mobile-only">
        <div v-for="band in bands" :key="band.id" class="band-card">
          <div class="band-card-header">
            <span class="band-name">{{ band.name }}</span>
            <span class="band-freq">{{ band.freq }}</span>
          </div>
          <div class="band-card-body">
            <div class="band-card-row">
              <div class="band-card-label">状况:</div>
              <div class="band-card-value">{{ band.condition }}</div>
            </div>
            <div class="band-card-row">
              <div class="band-card-label">距离:</div>
              <div class="band-card-value">{{ band.distance }}</div>
            </div>
            <div class="band-card-row">
              <div class="band-card-label">可靠性:</div>
              <div class="band-card-value">
                <div class="reliability-bar-container">
                  <div 
                    class="reliability-bar" 
                    :style="{ width: band.reliability + '%', backgroundColor: getReliabilityColor(band.reliability) }"
                  ></div>
                  <div class="reliability-text">{{ band.reliability }}%</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="card info-section">
      <h4>传播预测说明</h4>
      <p>此预测基于当前的太阳活动和地磁条件，为业余无线电操作提供参考。</p>
      <p>太阳流量指数(SFI)越高，高频段传播越好；K指数越低，传播条件越稳定。</p>
      <p>预测结果仅供参考，实际传播情况可能因地理位置、天气和其他因素而异。</p>
    </div>
  </div>
</template>

<style scoped>
.propagation-predictor {
  max-width: 800px;
  margin: 0 auto;
  padding: 20px;
  font-family: Arial, sans-serif;
}

h2 {
  color: #333;
  text-align: center;
  margin-bottom: 10px;
  font-size: 28px;
}

.subtitle {
  text-align: center;
  color: #666;
  margin-bottom: 20px;
  font-size: 16px;
}

.card {
  background-color: #fff;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 20px;
}

.input-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

h4 {
  margin-top: 0;
  margin-bottom: 15px;
  color: #333;
  font-size: 18px;
  border-bottom: 1px solid #eee;
  padding-bottom: 8px;
}

.solar-data {
  position: relative;
}

.solar-info {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px; /* 减小间距 */
  margin-bottom: 12px; /* 减小底部间距 */
}

.solar-item {
  display: flex;
  align-items: baseline;
  font-size: 13px; /* 减小字体大小 */
}

.solar-label {
  min-width: 120px; /* 标签宽度 */
  font-weight: bold;
  color: #555;
}

.solar-value {
  font-family: monospace;
  font-size: 1em; /* 调整字体大小 */
}

.loading-indicator {
  text-align: center;
  padding: 20px;
  color: #666;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.error {
  color: #d32f2f;
  text-align: center;
  padding: 10px;
}

.input-group {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.input-container {
  display: flex;
  align-items: center;
  flex: 1;
}

.input-container input,
.input-container select {
  width: 70px; /* 固定宽度 */
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.input-hint {
  margin-left: 10px;
  color: #777;
  font-size: 12px;
  white-space: nowrap;
}

button {
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 10px 15px;
  font-size: 16px;
  cursor: pointer;
  transition: background-color 0.2s;
}

button:hover {
  background-color: #388E3C;
}

button:disabled {
  background-color: #aaa;
  cursor: not-allowed;
}

.refresh-btn {
  background-color: #2196F3;
  display: block;
  margin: 0 auto;
  transition: background-color 0.3s, opacity 0.3s;
}

.refresh-btn:hover {
  background-color: #0b7dda;
}

.refresh-btn:disabled {
  background-color: #90CAF9;
  cursor: wait;
  opacity: 0.8;
}

.update-btn {
  display: block;
  margin: 20px auto 0;
}

.results-section {
  margin-bottom: 20px;
  overflow-x: auto; /* 添加水平滚动 */
}

.band-table {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  min-width: 600px;
  font-size: 13px; /* 减小字体大小 */
}

.band-header {
  display: grid;
  grid-template-columns: 0.7fr 1fr 0.7fr 0.7fr 1.2fr; /* 调整列宽比例 */
  background-color: #4CAF50;
  color: white;
  font-weight: bold;
}

.band-row {
  display: grid;
  grid-template-columns: 0.7fr 1fr 0.7fr 0.7fr 1.2fr; /* 与header保持一致 */
  background-color: white;
  border-bottom: 1px solid #eee;
}

.band-row:nth-child(even) {
  background-color: #f9f9f9;
}

.band-row:last-child {
  border-bottom: none;
}

.band-cell {
  padding: 8px 10px; /* 减小内边距 */
  font-size: 13px; /* 减小字体大小 */
  display: flex;
  align-items: center;
  white-space: nowrap; /* 防止内容换行 */
  overflow: hidden;
  text-overflow: ellipsis;
}

.band-name {
  font-weight: bold;
}

.band-freq {
  color: white;
}

.reliability-bar-container {
  width: 100%;
  height: 16px; /* 减小高度 */
  background-color: #eee;
  border-radius: 8px; /* 减小圆角 */
  overflow: hidden;
  position: relative;
}

.reliability-bar {
  height: 100%;
  transition: width 0.5s ease;
}

.reliability-text {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #333333;
  font-weight: bold;
  font-size: 11px; /* 减小字体大小 */
  text-shadow: 0 0 2px rgba(255, 255, 255, 0.7);
}

.info-section {
  background-color: #f5f9ff;
  border-left: 4px solid #1976d2;
  padding: 15px;
  border-radius: 5px;
}

/* 移动端适配 */
@media (max-width: 768px) {
  .input-section {
    grid-template-columns: 1fr;
  }
  
  .input-group {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .input-group label {
    margin-bottom: 8px;
    min-width: auto;
  }
  
  .input-wrapper {
    width: 100%;
  }
  
  .band-table {
    font-size: 13px;
  }
  
  .band-cell {
    padding: 10px 8px;
  }
  
  h2 {
    font-size: 24px;
  }
  
  .subtitle {
    font-size: 14px;
  }
  
  /* 移除滑动提示 */
  /* .results-section::after {
    content: "← 左右滑动查看更多 →";
    display: block;
    text-align: center;
    color: #666;
    font-size: 12px;
    margin-top: 8px;
    padding: 4px;
    background-color: #f5f5f5;
    border-radius: 4px;
  } */
}

/* 桌面/移动设备显示控制 */
@media (max-width: 768px) {
  .desktop-only {
    display: none;
  }
}

@media (min-width: 769px) {
  .mobile-only {
    display: none;
  }
}

/* 移动端卡片式布局样式 */
.band-card {
  background-color: white;
  border-radius: 6px; /* 减小圆角 */
  margin-bottom: 8px; /* 减小卡片间距 */
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.band-card-header {
  background-color: #4CAF50;
  color: white;
  padding: 6px 12px; /* 减小内边距 */
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.band-card-header .band-name {
  font-weight: bold;
  font-size: 14px; /* 减小字体大小 */
}

.band-card-header .band-freq {
  font-size: 12px; /* 减小字体大小 */
  opacity: 0.9;
}

.band-card-body {
  padding: 8px 12px; /* 减小内边距 */
}

.band-card-row {
  display: flex;
  margin-bottom: 6px; /* 减小行间距 */
  align-items: center;
}

.band-card-row:last-child {
  margin-bottom: 0;
}

.band-card-label {
  width: 60px; /* 减小标签宽度 */
  font-weight: 500;
  color: #555;
  font-size: 13px; /* 减小字体大小 */
}

.band-card-value {
  flex: 1;
  font-size: 13px; /* 减小字体大小 */
}

/* 调整可靠性条在移动端的样式 */
.band-card .reliability-bar-container {
  height: 14px; /* 进一步减小高度 */
}

.band-card .reliability-text {
  font-size: 10px; /* 减小字体大小 */
}

/* 调整卡片网格布局，在中等屏幕上显示两列 */
@media (min-width: 500px) and (max-width: 768px) {
  .mobile-only {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
  
  .band-card {
    margin-bottom: 0; /* 移除底部边距，因为使用了grid gap */
  }
}

/* 在较大的移动设备上显示三列 */
@media (min-width: 650px) and (max-width: 768px) {
  .mobile-only {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* 调整卡片内容在较小屏幕上的布局 */
@media (max-width: 360px) {
  .band-card-label {
    width: 50px;
    font-size: 12px;
  }
  
  .band-card-value {
    font-size: 12px;
  }
}

/* 输入部分整体布局优化 */
.input-section {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 15px; /* 减小间距 */
  margin-bottom: 15px; /* 减小底部间距 */
}

/* 卡片样式优化 */
.card {
  background-color: #fff;
  border-radius: 8px; /* 减小圆角 */
  padding: 15px; /* 减小内边距 */
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08); /* 更轻的阴影 */
  margin-bottom: 15px; /* 减小底部间距 */
}

/* 标题样式优化 */
h4 {
  margin-top: 0;
  margin-bottom: 12px; /* 减小底部间距 */
  color: #333;
  font-size: 16px; /* 减小字体大小 */
  border-bottom: 1px solid #eee;
  padding-bottom: 6px; /* 减小底部内边距 */
}

/* 太阳数据部分优化 */
.solar-info {
  display: grid;
  grid-template-columns: 1fr;
  gap: 6px; /* 减小间距 */
  margin-bottom: 12px; /* 减小底部间距 */
}

.solar-item {
  display: flex;
  align-items: baseline;
  font-size: 13px; /* 减小字体大小 */
}

.solar-label {
  min-width: 120px; /* 减小标签宽度 */
  font-weight: bold;
  color: #555;
}

.solar-value {
  font-family: monospace;
  font-size: 1em; /* 调整字体大小 */
}

/* 移动端太阳数据布局优化 */
@media (max-width: 768px) {
  .solar-info {
    display: grid;
    grid-template-columns: repeat(2, 1fr); /* 两列布局 */
    gap: 8px;
  }
  
  .solar-item {
    padding: 3px 0;
  }
  
  .solar-value {
    font-size: 12px;
    white-space: nowrap; /* 防止换行 */
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  /* 更新时间单独占一行 */
  .solar-item:last-child {
    grid-column: 1 / -1;
  }
}

/* 超小屏幕优化 */
@media (max-width: 400px) {
  .solar-info {
    grid-template-columns: 1fr; /* 单列布局 */
    gap: 5px;
  }
  
  .solar-value {
    font-size: 11px;
  }
}

/* 刷新按钮优化 */
.refresh-btn {
  padding: 6px 12px; /* 减小内边距 */
  font-size: 13px; /* 减小字体大小 */
}

/* 传播参数部分优化 */
.manual-inputs {
  padding: 15px; /* 减小内边距 */
}

.input-group {
  margin-bottom: 10px; /* 减小底部间距 */
}

.input-group label {
  font-size: 13px; /* 减小字体大小 */
  min-width: 80px; /* 减小标签宽度 */
}

.input-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
}

.input-wrapper input,
.input-wrapper select {
  padding: 6px 10px; /* 减小内边距 */
  font-size: 14px; /* 减小字体大小 */
}

.input-hint {
  font-size: 12px; /* 减小字体大小 */
  margin-left: 8px; /* 减小左边距 */
}

.update-btn {
  padding: 6px 12px; /* 减小内边距 */
  font-size: 13px; /* 减小字体大小 */
  margin-top: 8px; /* 减小顶部间距 */
}

/* 移动端适配优化 */
@media (max-width: 768px) {
  .input-section {
    grid-template-columns: 1fr;
    gap: 12px; /* 进一步减小间距 */
  }
  
  .card {
    padding: 12px; /* 进一步减小内边距 */
  }
  
  .solar-item {
    font-size: 12px; /* 进一步减小字体大小 */
  }
  
  .solar-label {
    min-width: 110px; /* 进一步减小标签宽度 */
  }
  
  .input-group {
    margin-bottom: 8px; /* 进一步减小底部间距 */
  }
  
  .input-group label {
    margin-bottom: 4px; /* 减小底部间距 */
  }
  
  /* 在小屏幕上优化输入组件布局 */
  .input-wrapper {
    flex-wrap: wrap;
  }
  
  .input-hint {
    width: 100%;
    margin-left: 0;
    margin-top: 4px;
    color: #666;
  }
  
  /* 在小屏幕上优化太阳数据布局 */
  .solar-info {
    display: grid;
    grid-template-columns: repeat(2, 1fr); /* 两列布局 */
    gap: 8px;
  }
  
  .solar-item {
    flex-direction: column;
    align-items: flex-start;
  }
  
  .solar-label {
    min-width: auto;
    margin-bottom: 2px;
    font-size: 11px;
    color: #666;
  }
  
  .solar-value {
    font-size: 13px;
  }
  
  /* 更新时间单独占一行 */
  .solar-item:last-child {
    grid-column: 1 / -1;
  }
}

/* 超小屏幕优化 */
@media (max-width: 360px) {
  .card {
    padding: 10px; /* 进一步减小内边距 */
  }
  
  h4 {
    font-size: 15px;
    margin-bottom: 10px;
    padding-bottom: 5px;
  }
  
  .solar-info {
    grid-template-columns: 1fr; /* 单列布局 */
  }
  
  .input-wrapper input,
  .input-wrapper select {
    padding: 5px 8px;
    font-size: 13px;
  }
}

/* 输入组样式调整 */
.input-group {
  margin-bottom: 15px;
  display: flex;
  align-items: center;
}

.input-group label {
  min-width: 80px;
  margin-right: 10px;
  font-weight: 500;
  color: #555;
}

.input-container {
  display: flex;
  align-items: center;
  flex: 1;
}

.input-container input,
.input-container select {
  width: 70px; /* 固定宽度 */
  padding: 8px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.input-hint {
  margin-left: 10px;
  color: #777;
  font-size: 12px;
  white-space: nowrap;
}

/* 移动端适配 - 保持水平布局，增加输入框宽度 */
@media (max-width: 768px) {
  .input-section {
    grid-template-columns: 1fr;
  }
  
  /* 移动端输入组布局 */
  .input-group {
    margin-bottom: 12px;
    display: flex;
    flex-direction: row;
    align-items: center;
  }
  
  .input-group label {
    min-width: 55px; /* 减小标签宽度 */
    margin-right: 5px;
    font-size: 14px;
    white-space: nowrap; /* 防止标签换行 */
  }
  
  .input-container {
    display: flex;
    align-items: center;
  }
  
  .input-container input,
  .input-container select {
    width: 180px; /* 增加输入框宽度 */
    padding: 6px 8px;
    font-size: 13px;
  }
  
  .input-hint {
    margin-left: 5px;
    font-size: 11px;
  }
}

/* 超小屏幕优化 */
@media (max-width: 400px) {
  .input-group {
    margin-bottom: 10px;
  }
  
  .input-group label {
    min-width: 45px; /* 进一步减小标签宽度 */
    font-size: 13px;
    margin-right: 3px;
  }
  
  .input-container input,
  .input-container select {
    width: 55px; /* 增加输入框宽度，但仍比正常屏幕小 */
    padding: 5px 6px;
    font-size: 12px;
  }
  
  .input-hint {
    margin-left: 3px;
    font-size: 10px;
  }
}

/* 添加深色模式适配 */
@media (prefers-color-scheme: dark) {
  .propagation-predictor {
    color: #e0e0e0;
  }
  
  h2, h4 {
    color: #e0e0e0;
  }
  
  .subtitle {
    color: #bbb;
  }
  
  .card {
    background-color: #252525;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }
  
  .solar-data {
    background-color: #1e1e1e;
  }
  
  .solar-info {
    border-color: #444;
  }
  
  .solar-item {
    color: #bbb;
  }
  
  .solar-value {
    color: #e0e0e0;
  }
  
  .solar-updated {
    color: #999;
  }
  
  .error {
    color: #ff6b6b;
    background-color: rgba(255, 107, 107, 0.1);
  }
  
  .input-group label {
    color: #bbb;
  }
  
  .input-container input, .input-container select {
    background-color: #333;
    color: #e0e0e0;
    border-color: #555;
  }
  
  .input-container input:focus, .input-container select:focus {
    border-color: #4db6ff;
  }
  
  .input-hint {
    color: #999;
  }
  
  .update-btn {
    background-color: #1976d2;
  }
  
  .update-btn:hover {
    background-color: #1565c0;
  }
  
  .refresh-btn {
    background-color: #333;
    color: #e0e0e0;
  }
  
  .refresh-btn:hover {
    background-color: #444;
  }
  
  .band-header {
    background-color: #333;
    color: #e0e0e0;
    border-color: #555;
  }
  
  .band-row {
    border-color: #444;
  }
  
  .band-row:hover {
    background-color: #2a2a2a;
  }
  
  .band-cell {
    color: #e0e0e0;
  }
  
  .band-name {
    color: white;
  }
  
  .status-excellent {
    color: #81c784;
  }
  
  .status-good {
    color: #aed581;
  }
  
  .status-fair {
    color: #ffd54f;
  }
  
  .status-poor {
    color: #ff8a65;
  }
  
  .status-very-poor {
    color: #e57373;
  }
  
  .reliability-bar-container {
    background-color: #444;
  }
  
  .band-card {
    background-color: #333;
    border-color: #444;
  }
  
  .band-card-header {
    border-bottom-color: #444;
  }
  
  .band-card-name {
    color: #4db6ff;
  }
  
  .band-card-freq {
    color: #bbb;
  }
  
  .band-card-label {
    color: #999;
  }
  
  .band-card-value {
    color: #e0e0e0;
  }
  
  .info-section {
    color: #bbb;
  }
}
</style>


